import { useMemo, useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { MonthSelector } from '@/components/month-selector'
import { FilterChip } from '@/components/filter-chip'
import {
  SCOPE_LABEL,
  SCOPE_OPTIONS,
  EXPENSE_TYPE_LABEL,
  EXPENSE_TYPE_OPTIONS,
  PAID_BY_LABEL,
  buildPaidByOptions,
} from '@/components/filter-chip-options'
import {
  CategoryDetailSheet,
  CategorySummarySection,
  CategoryDailyBarsSection,
  CategoryStoreRankingSection,
  CategorySubcategorySection,
  CategoryTransactionsFooter,
} from '@/components/report/category-detail'
import { SummaryHero } from '@/components/report/summary-hero'
import { InsightsCard } from '@/components/report/insights-card'
import { PaceCard } from '@/components/report/pace-card'
import { FrequentMerchantsCard } from '@/components/report/frequent-merchants-card'
import { CategoryDeviationCard } from '@/components/report/category-deviation-card'
import { WeekdayCard } from '@/components/report/weekday-card'
import { MonthlyTrendCard } from '@/components/report/monthly-trend-card'
import { getCurrentMonth, getDaysInMonth, shiftMonth } from '@/lib/utils'
import {
  useCategories,
  useExpenseType,
  usePaidBy,
  useProfiles,
  useReportStatistics,
  useScope,
} from '@/lib/hooks'
import { DEFAULT_CATEGORY_COLOR } from '@/lib/constants'

export default function ReportPage() {
  // Filters
  const { scope, setScope } = useScope()
  const [month, setMonth] = useState(getCurrentMonth())
  const { expenseType, setExpenseType } = useExpenseType()
  const { paidBy, setPaidBy } = usePaidBy()
  const { data: profiles = [] } = useProfiles()
  const paidByOptions = useMemo(() => buildPaidByOptions(profiles), [profiles])

  // Drill-down state — kept as two pieces of state so the bottom-sheet close
  // animation runs to completion before we drop the selected category.
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [detailSheetOpen, setDetailSheetOpen] = useState(false)

  // Categories → color lookup (parent-level only)
  const { data: categories = [] } = useCategories()
  const getCategoryColor = useMemo(() => {
    const m = new Map<string, string>()
    for (const cat of categories) {
      if (cat.parent_id === null && cat.color) m.set(cat.name, cat.color)
    }
    return (name: string) => m.get(name) ?? DEFAULT_CATEGORY_COLOR
  }, [categories])

  // All statistics + budget data for the page
  const {
    isLoading,
    filteredTx,
    stats,
    baselineTotal,
    deviations,
    categoryDrilldown,
    pace,
    dayOfWeek,
    frequent,
    insights,
    budgetByCategory,
    trendData,
  } = useReportStatistics(month)

  const heroTitle =
    expenseType === 'fixed'
      ? '今月の固定支出'
      : expenseType === 'variable'
        ? '今月の変動支出'
        : '今月の支出合計'

  return (
    <div className="bg-background">
      <MonthSelector month={month} onMonthChange={setMonth} />

      <div className="flex gap-2 px-4 pt-3 pb-1">
        <FilterChip label={SCOPE_LABEL} value={scope} onChange={setScope} options={SCOPE_OPTIONS} />
        <FilterChip
          label={EXPENSE_TYPE_LABEL}
          value={expenseType}
          onChange={setExpenseType}
          options={EXPENSE_TYPE_OPTIONS}
        />
        <FilterChip
          label={PAID_BY_LABEL}
          value={paidBy}
          onChange={setPaidBy}
          options={paidByOptions}
        />
      </div>

      {isLoading ? (
        <ReportSkeleton />
      ) : filteredTx.length === 0 ? (
        <div className="space-y-4 p-4">
          <SummaryHero stats={stats} baselineTotal={baselineTotal} title={heroTitle} />
          <div className="py-10 text-center text-sm text-muted-foreground">
            この月の取引データがありません
          </div>
        </div>
      ) : (
        <div className="space-y-4 p-4">
          <SummaryHero stats={stats} baselineTotal={baselineTotal} title={heroTitle} />
          <InsightsCard insights={insights} />
          <PaceCard pace={pace} stats={stats} baselineTotal={baselineTotal} />
          <FrequentMerchantsCard merchants={frequent} monthlyTotal={stats.total} />
          <CategoryDeviationCard
            deviations={deviations}
            getColor={getCategoryColor}
            onSelectCategory={(name) => {
              setSelectedCategory(name)
              setDetailSheetOpen(true)
            }}
            budgetByCategory={budgetByCategory}
          />
          <WeekdayCard stats={dayOfWeek} />
          <MonthlyTrendCard trendData={trendData} currentMonth={month} />
        </div>
      )}

      {selectedCategory && (() => {
        const prev = trendData.find((t) => t.month === shiftMonth(month, -1))
        const previousMonthAmount = prev ? Number(prev[selectedCategory]) || 0 : null
        return (
          <CategoryDetailSheet
            open={detailSheetOpen}
            onOpenChange={setDetailSheetOpen}
            categoryName={selectedCategory}
            categoryColor={getCategoryColor(selectedCategory)}
            categoryAmount={categoryDrilldown.get(selectedCategory)?.amount ?? 0}
            monthlyTotal={stats.total}
            filteredTransactions={filteredTx}
            currentMonth={month}
          >
            <CategorySummarySection previousMonthAmount={previousMonthAmount} />
            <CategoryDailyBarsSection daysInMonth={getDaysInMonth(month)} />
            <CategoryStoreRankingSection />
            <CategorySubcategorySection
              subcategories={categoryDrilldown.get(selectedCategory)?.subcategories ?? []}
            />
            <CategoryTransactionsFooter
              href={`/transactions?month=${month}&category=${encodeURIComponent(selectedCategory)}`}
            />
          </CategoryDetailSheet>
        )
      })()}
    </div>
  )
}

function ReportSkeleton() {
  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardContent>
          <Skeleton className="h-4 w-28" />
          <Skeleton className="mt-2 h-9 w-40" />
          <Skeleton className="mt-1 h-3 w-48" />
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-4 w-24" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-44 w-full" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-4 w-24" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-8 flex-1" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
